<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  routePlansApi, type RoutePlan, type RoutePlanVisit, type RoutePlanCaregiver,
  type SkippedDuplicate,
} from '@/api/route-plans';
import { type VueCalSpecialHours } from 'vue-cal';
import { buildRoutePlanEvents, schedulesFromPool } from '@/utils/route-plan-calendar-events';
import { useAssignmentsByCaregiver } from '@/composables/useAssignmentsByCaregiver';
import { useCaregiverColors } from '@/composables/useRouteColors';
import { useMobile } from '@/composables/useMobile';
import { usePermissions } from '@/composables/usePermissions';
import { useRoutePlanBoardDragAndDrop } from '@/composables/useRoutePlanBoardDragAndDrop';
import { useRoutePlanBoardDialogs } from '@/composables/useRoutePlanBoardDialogs';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import RoutePlanCalendar from './route-plan-calendar.vue';
import RoutePlanRouteViews from './route-plan-route-views.vue';
import RouteVisitDialog from './route-visit-dialog.vue';
import RouteVisitCreateDialog from './route-visit-create-dialog.vue';
import RouteCaregiverAddDialog from './route-caregiver-add-dialog.vue';
import RouteCaregiverEditDialog from './route-caregiver-edit-dialog.vue';
import { hmToMinutes } from './route-caregiver-edit.utils';
import { byCategoryPriority, effectivePrioritized } from './route-plan-visit.utils';
import RouteBlockedConfirmDialog from './route-blocked-confirm-dialog.vue';
import RoutePlanApplyDialog from './route-plan-apply-dialog.vue';
import RouteShiftDialog from './route-shift-dialog.vue';
import RoutePlanCoverageDialog from './route-plan-coverage-dialog.vue';

interface Props {
  routePlan: RoutePlan;
}

const props = defineProps<Props>();
const emit = defineEmits<{ updated: []; applied: [] }>();

const { t } = useI18n();

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAY_MINUTES = HOURS_PER_DAY * MINUTES_PER_HOUR;
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const busy = ref(false);
const snackbar = ref<{ show: boolean; text: string; color: string }>({ show: false, text: '', color: 'info' });
function notify(text: string, color = 'info') {
  snackbar.value = { show: true, text, color };
}

const { mdAndUp } = useMobile();

const showPending = ref(true);

const placedVisitIds = computed(() => new Set(props.routePlan.routeAssignments.map(a => a.routePlanVisitId)));
const unplacedVisits = computed(() => props.routePlan.routePlanVisits.filter(v => !placedVisitIds.value.has(v.id)));

// Category filter over the pending list (client-side; options come from the plan's own
// visits). Value 0 is the "Sin categoría" bucket.
const UNCATEGORIZED_FILTER_VALUE = 0;
const pendingCategoryIds = ref<number[]>([]);
const hasVisitCategories = computed(() =>
  props.routePlan.routePlanVisits.some(v => v.serviceCategoryId !== null));
const pendingCategoryItems = computed(() => {
  const seen = new Map<number, string>();
  props.routePlan.routePlanVisits.forEach((visit) => {
    if (visit.serviceCategoryId) {
      seen.set(visit.serviceCategoryId, visit.serviceCategoryName ?? '');
    } else {
      seen.set(UNCATEGORIZED_FILTER_VALUE, t('routePlan.board.noCategory'));
    }
  });

  return [...seen.entries()].map(([value, title]) => ({ value, title }));
});

const pendingVisits = computed(() => {
  const filtered = pendingCategoryIds.value.length === 0 ? unplacedVisits.value :
    unplacedVisits.value.filter((visit) =>
      pendingCategoryIds.value.includes(visit.serviceCategoryId ?? UNCATEGORIZED_FILTER_VALUE));

  return [...filtered].sort(byCategoryPriority);
});

// Only data problems earn a warning: manual placement can't fix them and the optimizer
// will keep skipping the visit. A capacity miss needs no message — sitting in Pendientes
// is the signal, and drag-and-drop surfaces its own feedback at placement time.
const reasonByVisit = computed<Record<number, string>>(
  () => Object.fromEntries(props.routePlan.unassignedVisits
    .filter((entry) => entry.tier === 'input_error')
    .map((entry) => [entry.routePlanVisitId, entry.reasonText])),
);

// Shared with the map/timeline/list views so a caregiver keeps the same color across tabs.
const assignmentsByCaregiver = useAssignmentsByCaregiver(() => props.routePlan);
const { colorMap } = useCaregiverColors(assignmentsByCaregiver);

// Clicking a visit (pending item or calendar block) opens the editor dialog for it; clicking an
// empty calendar slot opens a picker to place a pending visit there; dragging works too.
const {
  dialogOpen, dialogVisit, dialogAssignment, prefillCaregiverId,
  prefillArrivalTime, createDialogOpen, createPrefill, addCaregiverOpen,
  editCaregiverOpen, editCaregiverRpcId, shiftDialogOpen,
  shiftDialogId, pickerOpen, pickerCaregiverId, pickerArrival,
  openVisit, onShiftClick, openCreateDialog, openCaregiverEdit, onEventClick, onCellClick,
  pickVisit, caregiverName,
} = useRoutePlanBoardDialogs({
  routePlan: () => props.routePlan,
  // The filtered list, so the slot-click picker offers exactly what the pending panel
  // shows (and falls through to the create dialog when the filter hides everything).
  pendingVisits: () => pendingVisits.value,
});

const {
  calendarVersion, visitTimeLabel, notifyPlacement, onPendingDragStart,
  onEventDrop, onShiftDrop, onExternalDrop, onDropAborted, onBreakDrop,
  blockedConfirm, confirmBlockedOverride, cancelBlockedOverride,
  draggingAssignmentId, onAssignmentDragStart, onAssignmentDragEnd,
  onUnassignDragOver, onUnassignDrop,
} = useRoutePlanBoardDragAndDrop({
  routePlan: () => props.routePlan,
  busy,
  notify,
  onUpdated: () => emit('updated'),
});

// vue-cal (5 rc) appends to its internal event store when the :events array is replaced,
// so a refetch that changed the plan would render the stale tiles NEXT TO the fresh ones
// (e.g. a shift moved from the edit dialog kept showing at its original time). Remounting
// on every plan change forces a clean redraw from the new data.
watch(() => props.routePlan, () => {
  calendarVersion.value += 1;
});

const schedules = computed(() => schedulesFromPool(props.routePlan.routePlanCaregivers));
const events = computed(() => {
  // Reading calendarVersion makes a bump rebuild FRESH event objects: after a rejected drop
  // the refetched plan can be structurally identical (so this computed wouldn't re-run) while
  // vue-cal has mutated the cached events in place — the remount must not redraw those.
  void calendarVersion.value;

  return buildRoutePlanEvents(props.routePlan.routeAssignments, {
    existingShifts: props.routePlan.caregiverExistingShifts,
    caregiverBreaks: props.routePlan.caregiverBreaks,
    date: props.routePlan.date,
    career: props.routePlan.career,
  });
});

// The shaded ranges before/after a caregiver's availability window (empty if fully available).
function offHourRanges(caregiver: RoutePlanCaregiver) {
  const from = hmToMinutes(caregiver.availableFrom);
  const to = hmToMinutes(caregiver.availableTo);
  const ranges: Array<{ from: number; to: number; class: string }> = [];
  if (from > 0) ranges.push({ from: 0, to: from, class: 'route-plan-off-hours' });
  if (to < DAY_MINUTES) ranges.push({ from: to, to: DAY_MINUTES, class: 'route-plan-off-hours' });

  return ranges;
}

// Shade each caregiver column outside its availability window (from the setup step), so the
// free, placeable slots read as the white gaps. The overlay is pointer-events:none, so it
// never blocks placing a visit.
const offHours = computed<VueCalSpecialHours>(() => {
  const [year, month, day] = props.routePlan.date.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(year!, month! - 1, day!).getDay()]!;
  const ranges: Record<number, Array<{ from: number; to: number; class: string }>> = {};

  for (const caregiver of props.routePlan.routePlanCaregivers) {
    const caregiverRanges = offHourRanges(caregiver);
    if (caregiverRanges.length) ranges[caregiver.caregiverId] = caregiverRanges;
  }

  return { [weekday]: { schedules: ranges } };
});

function onVisitSaved(warnings: string[]) {
  notifyPlacement(warnings);
  emit('updated');
}

// Day-coverage dialog: requirements vs existing shifts vs this plan's assignments.
const coverageOpen = ref(false);
const missingCount = computed(() => props.routePlan.missingExpectedVisits?.length ?? 0);

// Applying creates and rewrites shifts, so it needs shift write permissions.
const { hasPermission } = usePermissions();
const canApply = computed(() => hasPermission.value('shifts', 'create') && hasPermission.value('shifts', 'update'));

async function removeVisit(visit: RoutePlanVisit) {
  if (busy.value) return;
  busy.value = true;
  try {
    await routePlansApi.destroyVisit(props.routePlan.id, visit.id);
    emit('updated');
  } catch {
    notify(t('routePlan.board.removeError'), 'error');
  } finally {
    busy.value = false;
  }
}

// Back to the setup stepper: resets the plan to draft, wiping ALL placements (incl. manual).
const resetConfirmOpen = ref(false);

async function confirmReset() {
  if (busy.value) return;
  busy.value = true;
  try {
    await routePlansApi.reset(props.routePlan.id);
    resetConfirmOpen.value = false;
    emit('updated');
  } catch {
    notify(t('routePlan.saveError'), 'error');
  } finally {
    busy.value = false;
  }
}

// Apply with a dry run of the apply gate (blockers/warnings/skips) in the dialog.
const applyDialog = ref(false);

function onApplied(skipped: SkippedDuplicate[]) {
  if (skipped.length) notify(t('routePlan.board.applySkipped', { count: skipped.length }), 'warning');
  emit('applied');
}

</script>

<template>
  <v-layout class="route-plan-board">
    <!-- The panel doubles as an unassign drop target while an assignment block is mid-drag:
         dropping it here removes the placement and the visit returns to pending. -->
    <v-navigation-drawer
      v-model="showPending"
      location="left"
      :permanent="mdAndUp"
      :temporary="!mdAndUp"
      absolute
      width="320"
      :class="{ 'route-plan-board__unassign-target': draggingAssignmentId !== null }"
      @dragover="onUnassignDragOver"
      @drop="onUnassignDrop"
    >
      <v-alert
        v-if="draggingAssignmentId !== null"
        type="info"
        variant="tonal"
        density="compact"
        icon="mdi-tray-arrow-down"
        class="ma-2 text-caption"
      >
        {{ t('routePlan.board.dropToUnassign') }}
      </v-alert>
      <v-list-item nav>
        <v-list-item-title class="text-subtitle-1 font-weight-bold">
          {{ t('routePlan.board.pending', { count: unplacedVisits.length }) }}
        </v-list-item-title>
        <template #append>
          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            size="small"
            @click="showPending = false"
          />
        </template>
      </v-list-item>
      <v-alert
        type="info"
        variant="tonal"
        density="compact"
        class="ma-2 text-caption"
      >
        {{ t('routePlan.board.hint') }}
      </v-alert>
      <v-select
        v-if="hasVisitCategories"
        v-model="pendingCategoryIds"
        :items="pendingCategoryItems"
        :label="t('routePlan.board.categoryFilter')"
        class="mx-2"
        density="compact"
        variant="outlined"
        multiple
        clearable
        hide-details
      />
      <v-list
        v-if="pendingVisits.length"
        density="compact"
      >
        <v-list-item
          v-for="visit in pendingVisits"
          :key="visit.id"
          class="route-plan-board__pending-item"
          draggable="true"
          @dragstart="onPendingDragStart($event, visit)"
          @click="openVisit(visit.id)"
        >
          <v-list-item-title>{{ visit.patientFullName }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ visitTimeLabel(visit) }}
          </v-list-item-subtitle>
          <v-chip
            v-if="visit.serviceCategoryName"
            size="x-small"
            variant="tonal"
            color="primary"
            class="mt-1"
          >
            {{ visit.serviceCategoryName }}
          </v-chip>
          <v-chip
            v-if="effectivePrioritized(visit)"
            size="x-small"
            variant="tonal"
            color="warning"
            prepend-icon="mdi-star"
            class="mt-1 ms-1"
          >
            {{ t('routePlan.board.prioritized') }}
          </v-chip>
          <template #append>
            <v-tooltip
              v-if="reasonByVisit[visit.id]"
              location="top"
              open-on-click
              max-width="360"
            >
              <template #activator="{ props: tooltipProps }">
                <v-icon
                  icon="mdi-alert-circle-outline"
                  color="error"
                  size="small"
                  class="me-1"
                  v-bind="tooltipProps"
                />
              </template>
              <span>{{ reasonByVisit[visit.id] }}</span>
            </v-tooltip>
            <v-btn
              icon="mdi-delete-outline"
              variant="text"
              size="small"
              :disabled="busy"
              @click.stop="removeVisit(visit)"
            />
          </template>
        </v-list-item>
      </v-list>
      <p
        v-else
        class="text-medium-emphasis pa-3"
      >
        {{ t('routePlan.board.allPlaced') }}
      </p>

      <!-- Pinned to the drawer bottom; the pending list scrolls independently above. -->
      <template #append>
        <div class="pa-2 d-flex flex-column ga-1 route-plan-board__drawer-actions">
          <v-btn
            block
            variant="tonal"
            color="primary"
            size="small"
            prepend-icon="mdi-plus"
            @click="openCreateDialog()"
          >
            {{ t('routePlan.addVisit.cta') }}
          </v-btn>
          <v-btn
            block
            variant="tonal"
            size="small"
            prepend-icon="mdi-account-plus"
            @click="addCaregiverOpen = true"
          >
            {{ t('routePlan.addCaregiver.cta') }}
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main class="route-plan-board__main">
      <div class="d-flex flex-wrap align-center mb-3 ga-2">
        <!-- The panel closes with its own chevron; this only reopens it. -->
        <v-btn
          v-if="!showPending"
          variant="text"
          prepend-icon="mdi-clipboard-list-outline"
          @click="showPending = true"
        >
          {{ t('routePlan.board.showPending', { count: unplacedVisits.length }) }}
        </v-btn>
        <span class="text-body-1 ml-4">
          {{ t('routePlan.board.placedSummary', {
            placed: routePlan.assignedCount, total: routePlan.routePlanVisits.length,
          }) }}
        </span>
      </div>

      <route-plan-route-views
        :route-plan="routePlan"
        :assignments-by-caregiver="assignmentsByCaregiver"
        :color-map="colorMap"
        read-only
      >
        <template #append>
          <v-badge
            :content="missingCount"
            :model-value="missingCount > 0"
            color="error"
            offset-x="4"
            class="mr-3"
          >
            <v-btn
              variant="text"
              color="primary"
              size="small"
              prepend-icon="mdi-clipboard-check-outline"
              @click="coverageOpen = true"
            >
              {{ t('routePlan.coverage.cta') }}
            </v-btn>
          </v-badge>
        </template>
        <template #calendar>
          <route-plan-calendar
            :key="calendarVersion"
            :events="events"
            :schedules="schedules"
            :special-hours="offHours"
            :view-date="routePlan.date"
            :loading="busy"
            @event-click="onEventClick"
            @cell-click="onCellClick"
            @event-drop="onEventDrop"
            @external-drop="onExternalDrop"
            @break-drop="onBreakDrop"
            @shift-drop="onShiftDrop"
            @shift-click="onShiftClick"
            @schedule-click="openCaregiverEdit"
            @drop-aborted="onDropAborted"
            @assignment-drag-start="onAssignmentDragStart"
            @assignment-drag-end="onAssignmentDragEnd"
          />
        </template>
      </route-plan-route-views>

      <!-- Wizard footer: always visible while working on the tall calendar. -->
      <div class="route-plan-board__footer d-flex align-center px-4 py-3">
        <v-btn
          variant="text"
          prepend-icon="mdi-arrow-left"
          :disabled="busy"
          @click="resetConfirmOpen = true"
        >
          {{ t('routePlan.backToEdit') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="canApply"
          color="primary"
          variant="flat"
          :loading="busy"
          prepend-icon="mdi-check"
          @click="applyDialog = true"
        >
          {{ t('routePlan.board.applyAndCreate') }}
        </v-btn>
      </div>
    </v-main>

    <route-visit-dialog
      v-model="dialogOpen"
      :route-plan="routePlan"
      :visit="dialogVisit"
      :assignment="dialogAssignment"
      :prefill-caregiver-id="prefillCaregiverId"
      :prefill-arrival-time="prefillArrivalTime"
      @saved="onVisitSaved"
    />

    <route-visit-create-dialog
      v-model="createDialogOpen"
      :route-plan="routePlan"
      :prefill-caregiver-id="createPrefill?.caregiverId ?? null"
      :prefill-start-time="createPrefill?.arrivalTime ?? null"
      @saved="onVisitSaved"
    />

    <route-caregiver-add-dialog
      v-model="addCaregiverOpen"
      :route-plan="routePlan"
      @saved="emit('updated')"
    />

    <route-caregiver-edit-dialog
      v-model="editCaregiverOpen"
      :route-plan="routePlan"
      :route-plan-caregiver-id="editCaregiverRpcId"
      @saved="onVisitSaved"
    />

    <route-shift-dialog
      v-model="shiftDialogOpen"
      :route-plan="routePlan"
      :shift-id="shiftDialogId"
      @saved="onVisitSaved"
    />

    <route-plan-coverage-dialog
      v-model="coverageOpen"
      :route-plan="routePlan"
      @saved="onVisitSaved"
    />

    <responsive-dialog
      v-model="pickerOpen"
      :max-width="420"
      :title="t('routePlan.board.placePrompt')"
    >
      <template #default>
        <v-chip
          size="small"
          color="primary"
          variant="tonal"
          prepend-icon="mdi-map-marker-outline"
          class="mb-3"
        >
          {{ caregiverName(pickerCaregiverId) }} · {{ pickerArrival }}
        </v-chip>
        <v-list
          density="comfortable"
          class="pa-0"
        >
          <v-list-item
            v-for="visit in pendingVisits"
            :key="visit.id"
            rounded="lg"
            prepend-icon="mdi-calendar-plus"
            @click="pickVisit(visit)"
          >
            <v-list-item-title>{{ visit.patientFullName }}</v-list-item-title>
            <v-list-item-subtitle>{{ visitTimeLabel(visit) }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </template>
      <template #actions="{ close }">
        <v-btn
          variant="text"
          @click="close"
        >
          {{ t('routePlan.cancel') }}
        </v-btn>
      </template>
    </responsive-dialog>

    <responsive-dialog
      v-model="resetConfirmOpen"
      :max-width="420"
    >
      <template #default="{ close }">
        <v-card class="pa-2">
          <v-card-title>{{ t('routePlan.backToEdit') }}</v-card-title>
          <v-card-text>{{ t('routePlan.confirmReset') }}</v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="text"
              @click="close"
            >
              {{ t('routePlan.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :loading="busy"
              @click="confirmReset"
            >
              {{ t('routePlan.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </responsive-dialog>

    <route-blocked-confirm-dialog
      :model-value="blockedConfirm !== null"
      :caregiver-name="blockedConfirm?.caregiverName ?? ''"
      :patient-name="blockedConfirm?.patientName ?? ''"
      :busy="busy"
      @confirm="confirmBlockedOverride"
      @cancel="cancelBlockedOverride"
    />

    <route-plan-apply-dialog
      v-model="applyDialog"
      :route-plan="routePlan"
      @applied="onApplied"
      @updated="emit('updated')"
    />

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-layout>
</template>

<style scoped>
/* Pending visits live in a left v-navigation-drawer (permanent on desktop, temporary on
   mobile), like the shifts calendar's filters drawer. `absolute` scopes the drawer + its
   mobile scrim to this board instead of the whole app shell. */

.route-plan-board__main {
  /* Fill the viewport below the page header (~172px) even on short tabs (Cronograma/Lista),
     and push the wizard footer to the bottom — so both sticky bars always sit at the
     viewport edge instead of floating up when the content is short. */
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - 172px);
}

/* Pending visits can be dragged onto the calendar. */
.route-plan-board__pending-item {
  cursor: grab;
}

/* Lit while an assignment block is mid-drag: the panel is the unassign drop target. */
.route-plan-board__unassign-target {
  outline: 2px dashed rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

/* Vuetify's layout engine sets overflow: hidden INLINE on v-layout, which breaks
   position: sticky against the window scroll — hence the !important. Horizontal overflow
   stays clipped (overflow-x: clip, which unlike hidden creates no scroll container) so the
   closed drawer, which translates off-canvas to the left, doesn't leave a visible sliver. */

.route-plan-board.v-layout {
  overflow: visible !important;
  overflow-x: clip !important;
}

/* Drawer actions pinned at the bottom, echoing the board footer. The drawer spans the full
   page height (taller than the viewport), so its append area must stick to the WINDOW
   bottom, exactly like the wizard footer. */

.route-plan-board :deep(.v-navigation-drawer__append) {
  position: sticky;
  bottom: 0;
  background-color: #fff;
}

.route-plan-board__drawer-actions {
  border-top: 1px solid rgba(0, 0, 0, .08);
}

/* Wizard footer pinned to the viewport bottom while the board scrolls. */
.route-plan-board__footer {
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 5;
  background-color: #fff;
  border-top: 1px solid rgba(0, 0, 0, .08);
}
</style>
